import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import './AdminInstructors.css';
import AdminLayout from './AdminLayout';

/* ── helpers ────────────────────────────────────────────────── */
const openDoc = async (storedPath) => {
  if (!storedPath) return;
  try {
    const blob = await api.get(`/admin/view-file?path=${encodeURIComponent(storedPath)}`, { responseType: 'blob' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    alert('Document not available or not yet uploaded.');
  }
};

const DOC_GROUPS = [
  {
    title: 'Driving Licence',
    docs: [
      { label: 'Driving Licence (Front)', key: 'driving_licence_file' },
      { label: 'Driving Licence (Back)', key: 'driving_licence_back_file' },
    ],
  },
  {
    title: 'ADI Badge',
    docs: [
      { label: 'ADI Green Badge (Front)', key: 'green_badge_file' },
      { label: 'ADI Green Badge (Back)', key: 'green_badge_back_file' },
    ],
  },
  {
    title: 'Vehicle',
    docs: [
      { label: 'Insurance Certificate', key: 'insurance_certificate_file' },
      { label: 'MOT Certificate',       key: 'mot_certificate_file' },
    ],
  },
];

const countUploaded = (instr) =>
  DOC_GROUPS.flatMap((g) => g.docs).filter((d) => instr[d.key]).length;

const TOTAL_DOCS = DOC_GROUPS.flatMap((g) => g.docs).length;

/* ── sub-components ─────────────────────────────────────────── */
const DocRow = ({ label, path }) => (
  <div className="ai-doc-row">
    <span className={`ai-dot ${path ? 'ai-dot--ok' : 'ai-dot--missing'}`} />
    <span className="ai-doc-label">{label}</span>
    {path
      ? <button className="ai-btn-view" onClick={() => openDoc(path)}>View 👁</button>
      : <span className="ai-no-doc">Not uploaded</span>
    }
  </div>
);

/* ── main component ─────────────────────────────────────────── */
export default function AdminInstructors() {
  const navigate = useNavigate();
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [selected, setSelected] = useState(null);

  const fetchInstructors = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get('/admin/instructor-docs');
      setInstructors(data.instructors || data);
    } catch (err) {
      setError(err.message || 'Failed to load instructors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInstructors(); }, [fetchInstructors]);

  const toggleActive = async (instr) => {
    if (!window.confirm(`${instr.is_active ? 'Deactivate' : 'Activate'} instructor "${instr.full_name}"?`)) return;
    try {
      const data = await api.put(`/admin/instructors/${instr.id}/toggle-active`);
      setInstructors(prev => prev.map(i => i.id === instr.id ? { ...i, is_active: data.is_active } : i));
      if (selected?.id === instr.id) setSelected(prev => ({ ...prev, is_active: data.is_active }));
    } catch {
      alert('Failed to update instructor status');
    }
  };

  const filtered = instructors.filter((i) => {
    const matchSearch =
      !search ||
      i.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      i.instructor_code?.toLowerCase().includes(search.toLowerCase()) ||
      i.city?.toLowerCase().includes(search.toLowerCase()) ||
      i.email?.toLowerCase().includes(search.toLowerCase());
    const matchActive =
      filterActive === 'all' ||
      (filterActive === 'active' && i.is_active) ||
      (filterActive === 'inactive' && !i.is_active);
    return matchSearch && matchActive;
  });

  return (
    <AdminLayout pageTitle="Instructors">
    <div className="ai-page">
      {/* Header */}
      <div className="ai-header">
        <div>
          <h1 className="ai-title">Instructors &amp; Documents</h1>
          <p className="ai-sub">View and verify documents uploaded by approved instructors</p>
        </div>
      </div>

      {/* Controls */}
      <div className="ai-controls">
        <input
          className="ai-search"
          type="text"
          placeholder="Search by name, code, city or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="ai-filter-tabs">
          {['all', 'active', 'inactive'].map((v) => (
            <button
              key={v}
              className={`ai-filter-tab ${filterActive === v ? 'ai-filter-tab--active' : ''}`}
              onClick={() => setFilterActive(v)}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
        <span className="ai-count">{filtered.length} instructor{filtered.length !== 1 ? 's' : ''}</span>
        <button className="ap-refresh-btn" onClick={fetchInstructors} disabled={loading} title="Refresh">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transition: 'transform 0.4s', transform: loading ? 'rotate(360deg)' : 'none' }}>
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Content */}
      {loading && <div className="ai-status">Loading instructors…</div>}
      {error   && <div className="ai-status ai-status--error">{error}</div>}

      {!loading && !error && (
        <div className="ai-layout">
          {/* Table */}
          <div className="ai-table-wrap">
            <table className="ai-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>City</th>
                  <th>Vehicle</th>
                  <th>Paid Students</th>
                  <th>Docs</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="ai-empty">No instructors found</td></tr>
                )}
                {filtered.map((instr) => {
                  const uploaded = countUploaded(instr);
                  return (
                    <tr
                      key={instr.id}
                      className={selected?.id === instr.id ? 'ai-row ai-row--selected' : 'ai-row'}
                      onClick={() => setSelected(instr.id === selected?.id ? null : instr)}
                    >
                      <td>
                        <div className="ai-name">{instr.full_name}</div>
                        <div className="ai-email">{instr.email}</div>
                        {instr.contact_number && (
                          <div className="ai-email">{instr.contact_number}</div>
                        )}
                      </td>
                      <td><span className="ai-code">{instr.instructor_code}</span></td>
                      <td>{instr.city || '—'}</td>
                      <td>
                        {instr.vehicle_make
                          ? `${instr.vehicle_make} ${instr.vehicle_model || ''} (${instr.transmission_type || '—'})`
                          : '—'}
                      </td>
                      <td>
                        <span className="ai-students-count" title={`${instr.paid_students_count ?? 0} paid / ${instr.students_assigned ?? 0} total assigned`}>
                          {instr.paid_students_count ?? 0}
                          {(instr.students_assigned ?? 0) > (instr.paid_students_count ?? 0) && (
                            <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.78rem', marginLeft: 4 }}>
                              /{instr.students_assigned} total
                            </span>
                          )}
                        </span>
                      </td>
                      <td>
                        <span className={`ai-doc-count ${uploaded < TOTAL_DOCS ? 'ai-doc-count--warn' : 'ai-doc-count--full'}`}>
                          {uploaded}/{TOTAL_DOCS}
                        </span>
                      </td>
                      <td>
                        <span className={`ai-badge ${instr.is_active ? 'ai-badge--active' : 'ai-badge--inactive'}`}>
                          {instr.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="ai-actions-cell">
                        <button className="ai-btn-view-docs">
                          {selected?.id === instr.id ? 'Close ✕' : 'Docs 📎'}
                        </button>
                        <button
                          className={`ai-btn-toggle-active ${instr.is_active ? 'ai-btn-deactivate' : 'ai-btn-activate'}`}
                          onClick={(e) => { e.stopPropagation(); toggleActive(instr); }}
                          title={instr.is_active ? 'Deactivate instructor' : 'Activate instructor'}
                        >
                          {instr.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Doc panel */}
          {selected && (
            <div className="ai-doc-panel">
              <div className="ai-panel-header">
                <div>
                  <h2 className="ai-panel-name">{selected.full_name}</h2>
                  <span className="ai-code">{selected.instructor_code}</span>
                  {selected.referral_code && (
                    <span className="ai-referral-tag">Referral: {selected.referral_code}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    className={`ai-btn-toggle-active ${selected.is_active ? 'ai-btn-deactivate' : 'ai-btn-activate'}`}
                    onClick={() => toggleActive(selected)}
                  >
                    {selected.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button className="ai-close-panel" onClick={() => setSelected(null)}>✕</button>
                </div>
              </div>

              {selected.vehicle_make && (
                <div className="ai-vehicle-info">
                  🚗 {selected.vehicle_make} {selected.vehicle_model} {selected.vehicle_year && `(${selected.vehicle_year})`} · {selected.vehicle_registration} · {selected.transmission_type}
                </div>
              )}

              {selected.dvla_check_code && (
                <div className="ai-vehicle-info" style={{ fontSize: '0.85rem', color: '#334155' }}>
                  🪪 DVLA Check Code: <strong>{selected.dvla_check_code}</strong>
                </div>
              )}

              <div className="ai-doc-summary">
                <span className={`ai-doc-count ${countUploaded(selected) < TOTAL_DOCS ? 'ai-doc-count--warn' : 'ai-doc-count--full'}`}>
                  {countUploaded(selected)}/{TOTAL_DOCS} documents uploaded
                </span>
              </div>

              {DOC_GROUPS.map((group) => (
                <div key={group.title} className="ai-doc-group">
                  <h4 className="ai-group-title">{group.title}</h4>
                  {group.docs.map((d) => (
                    <DocRow key={d.key} label={d.label} path={selected[d.key]} />
                  ))}
                </div>
              ))}

              {/* Vehicle Photos (multi-upload — one row per photo) */}
              {(selected.vehicle_exterior_photos?.length > 0 || selected.vehicle_interior_photos?.length > 0 || selected.dual_controls_photos?.length > 0) && (
                <div className="ai-doc-group">
                  <h4 className="ai-group-title">Vehicle Photos</h4>
                  {selected.vehicle_exterior_photos?.map((path, i) => (
                    <DocRow key={`ext-${i}`} label={`Exterior Photo ${i + 1}`} path={path} />
                  ))}
                  {selected.vehicle_interior_photos?.map((path, i) => (
                    <DocRow key={`int-${i}`} label={`Interior Photo ${i + 1}`} path={path} />
                  ))}
                  {selected.dual_controls_photos?.map((path, i) => (
                    <DocRow key={`dual-${i}`} label={`Dual Controls Photo ${i + 1}`} path={path} />
                  ))}
                </div>
              )}

              {/* Signature */}
              {selected.signature_data && (
                <div className="ai-doc-group">
                  <h4 className="ai-group-title">Applicant Signature</h4>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginTop: 8 }}>
                    <img
                      src={selected.signature_data}
                      alt="Instructor signature"
                      style={{ maxWidth: '100%', maxHeight: 120, display: 'block', filter: 'invert(0)' }}
                    />
                  </div>
                </div>
              )}

              {/* Banking Info */}
              {(selected.account_holder_name || selected.bank_name || selected.sort_code || selected.account_number) && (
                <div className="ai-doc-group">
                  <h4 className="ai-group-title">Banking Details</h4>
                  <div className="ai-bank-grid">
                    {selected.account_holder_name && (
                      <div className="ai-bank-row">
                        <span className="ai-bank-label">Account Holder</span>
                        <span className="ai-bank-value">{selected.account_holder_name}</span>
                      </div>
                    )}
                    {selected.bank_name && (
                      <div className="ai-bank-row">
                        <span className="ai-bank-label">Bank</span>
                        <span className="ai-bank-value">{selected.bank_name}</span>
                      </div>
                    )}
                    {selected.sort_code && (
                      <div className="ai-bank-row">
                        <span className="ai-bank-label">Sort Code</span>
                        <span className="ai-bank-value ai-bank-value--mono">{selected.sort_code}</span>
                      </div>
                    )}
                    {selected.account_number && (
                      <div className="ai-bank-row">
                        <span className="ai-bank-label">Account Number</span>
                        <span className="ai-bank-value ai-bank-value--mono">{selected.account_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
    </AdminLayout>
  );
}
